export const ROUTE_DATA_STATES = [
  'initial-loading',
  'refreshing',
  'empty',
  'stale-cache',
  'partial',
  'permission',
  'hard-failure',
  'populated',
] as const;

export type RouteDataState = (typeof ROUTE_DATA_STATES)[number];

export const ROUTE_AUDIT_FIXTURES = ROUTE_DATA_STATES;

// Direct references are required so Next can inline the two public flags into
// the disposable client bundle. Tests may still supply an explicit environment.
const BUILD_NODE_ENV = process.env.NODE_ENV;
const BUILD_UI_AUDIT_FIXTURES = process.env.NEXT_PUBLIC_UI_AUDIT_FIXTURES;
const BUILD_UI_RELEASE_GATE = process.env.NEXT_PUBLIC_UI_RELEASE_GATE_BUILD;

export function isRouteDataState(value: string | null | undefined): value is RouteDataState {
  return Boolean(value && ROUTE_DATA_STATES.includes(value as RouteDataState));
}

export function auditFixtureState(value: string | null | undefined, environment: NodeJS.ProcessEnv = process.env): RouteDataState | null {
  const runtimeFlag = typeof window !== 'undefined' && window.location.hostname === 'localhost' && (window as Window & { __SCARLET_SYNC_AUDIT_FIXTURES__?: boolean }).__SCARLET_SYNC_AUDIT_FIXTURES__ === true;
  const explicitEnvironment = environment !== process.env;
  const nodeEnvironment = explicitEnvironment ? environment.NODE_ENV : BUILD_NODE_ENV;
  const auditFlag = explicitEnvironment ? environment.NEXT_PUBLIC_UI_AUDIT_FIXTURES : BUILD_UI_AUDIT_FIXTURES;
  const releaseGateFlag = explicitEnvironment ? environment.NEXT_PUBLIC_UI_RELEASE_GATE_BUILD : BUILD_UI_RELEASE_GATE;
  const productionReleaseGateBuild = nodeEnvironment === 'production' &&
    auditFlag === 'true' && releaseGateFlag === 'fixture-only-v1';
  const developmentFixture = nodeEnvironment !== 'production' && (auditFlag === 'true' || runtimeFlag);
  if (!productionReleaseGateBuild && !developmentFixture) return null;
  return isRouteDataState(value) ? value : null;
}

/**
 * A deliberately small contract for routes with independently refreshable data.
 * A failed region must retain a known timestamp and any still-usable siblings.
 */
export interface RouteDataContract<T> {
  state: RouteDataState;
  data?: T;
  retrievedAt?: string;
  sourceKind: 'live' | 'cached' | 'generated' | 'checked-in';
  failedRegions?: string[];
}

export function validateRouteDataContract<T>(contract: RouteDataContract<T>): string | null {
  if ((contract.sourceKind === 'cached' || contract.sourceKind === 'checked-in') && !contract.retrievedAt) return 'cached and checked-in data require a retrieval timestamp';
  if (contract.sourceKind === 'live' && contract.state === 'stale-cache') return 'stale cached data cannot claim to be live';
  if (['refreshing', 'stale-cache', 'partial'].includes(contract.state) && !contract.data) return `${contract.state} requires retained usable data`;
  if (contract.state === 'partial' && !contract.failedRegions?.length) return 'partial data must name failed regions';
  return null;
}

export function canRetainUsableData<T>(contract: RouteDataContract<T>): boolean {
  return Boolean(contract.data) && ['refreshing', 'stale-cache', 'partial'].includes(contract.state);
}

export function requiresRecoveryAction(state: RouteDataState): boolean {
  return ['stale-cache', 'partial', 'permission', 'hard-failure'].includes(state);
}

export function deriveRouteDataState({ initialLoading = false, refreshing = false, hasUsableData = false, error = false, permission = false, failedRegions = [] }: { initialLoading?: boolean; refreshing?: boolean; hasUsableData?: boolean; error?: boolean; permission?: boolean; failedRegions?: string[] }): RouteDataState {
  if (permission) return 'permission';
  if (initialLoading && !hasUsableData) return 'initial-loading';
  if (refreshing && hasUsableData) return 'refreshing';
  if (error && hasUsableData && failedRegions.length) return 'partial';
  if (error && hasUsableData) return 'stale-cache';
  if (error) return 'hard-failure';
  return hasUsableData ? 'populated' : 'empty';
}
